import { useState, useEffect } from "react";
import { Link, NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { Menu, ShoppingBag, MessageCircle, LogOut, BadgeCheck, ShieldCheck, ArrowRight, Package, X, Sparkles } from "lucide-react";
import AuthDialog from "@/components/AuthDialog";

const FOUNDER_EMAIL = "rektr.srm@gmail.com";

const NAV = [
  { name: "Home", path: "/", testid: "nav-link-home" },
  { name: "Marketplace", path: "/marketplace", testid: "nav-link-marketplace" },
  { name: "Membership", path: "/membership", testid: "nav-link-membership" },
  { name: "Help Center", path: "/help", testid: "nav-link-help" },
  { name: "Addresses", path: "/addresses", testid: "nav-link-addresses" },
];

function BrandMark() {
  return (
    <Link to="/" className="flex items-center gap-2.5 shrink-0" data-testid="brand-logo">
      <div className="h-9 w-9 rounded-md bg-ink text-white grid place-items-center font-display font-extrabold text-lg">
        R
      </div>
      <div className="leading-none">
        <div className="font-display font-extrabold text-[15px] tracking-tight text-ink">ReKTR</div>
        <div className="label-mono text-crimson mt-1 text-[9px]">SRM KTR</div>
      </div>
    </Link>
  );
}

export default function Layout() {
  const { user, ready, logout, membershipConfig } = useAuth();
  const [authOpen, setAuthOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const isFounder = user && (user.is_founder || user.email?.toLowerCase() === FOUNDER_EMAIL);

  const [unread, setUnread] = useState(0);
  const [dealsPending, setDealsPending] = useState(0);
  const [bannerHidden, setBannerHidden] = useState(false);
  useEffect(() => {
    if (!user) { setUnread(0); return; }
    let cancel = false;
    const poll = () => api.get("/unread-count").then(({ data }) => { if (!cancel) setUnread(data.count); }).catch(() => {});
    poll();
    const iv = setInterval(poll, 12000);
    return () => { cancel = true; clearInterval(iv); };
  }, [user, location.pathname]);

  useEffect(() => {
    if (!user) { setDealsPending(0); return; }
    let cancel = false;
    const poll = () => api.get("/deals/pending-count").then(({ data }) => { if (!cancel) setDealsPending(data.count || 0); }).catch(() => {});
    poll();
    const iv = setInterval(poll, 15000);
    return () => { cancel = true; clearInterval(iv); };
  }, [user, location.pathname]);

  const previewEndsAt = membershipConfig?.preview_ends_at;
  const previewActive = membershipConfig?.preview_active;
  const daysLeft = previewEndsAt ? Math.max(0, Math.ceil((new Date(previewEndsAt) - new Date()) / 86400000)) : 0;
  useEffect(() => {
    if (previewEndsAt && localStorage.getItem("rektr_preview_dismissed") === previewEndsAt) setBannerHidden(true);
  }, [previewEndsAt]);
  const dismissBanner = () => { if (previewEndsAt) localStorage.setItem("rektr_preview_dismissed", previewEndsAt); setBannerHidden(true); };

  const links = [...NAV];
  if (user) links.push({ name: "My Deals", path: "/deals", testid: "nav-link-deals" });
  if (isFounder) links.push({ name: "In-Web Data", path: "/admin", testid: "nav-link-admin" });

  const linkClass = ({ isActive }) =>
    `relative px-1 py-1 text-sm font-medium transition-colors ${
      isActive ? "text-ink" : "text-slate-500 hover:text-ink"
    }`;

  return (
    <div className="min-h-screen flex flex-col bg-[#F8FAFC]">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/85 border-b border-slate-200/80">
        <div className="mx-auto max-w-7xl px-4 lg:px-8 h-16 flex items-center justify-between gap-4">
          <BrandMark />

          <nav className="hidden lg:flex items-center gap-7">
            {links.map((l) => (
              <NavLink key={l.path} to={l.path} className={linkClass} data-testid={l.testid} end={l.path === "/"}>
                {({ isActive }) => (
                  <span className="inline-flex items-center gap-1.5">
                    {l.name === "In-Web Data" && <ShieldCheck size={14} className="text-crimson" />}
                    {l.name}
                    {l.path === "/deals" && dealsPending > 0 && (
                      <span className="min-w-[18px] h-[18px] px-1 rounded-full bg-crimson text-white text-[10px] font-bold grid place-items-center" data-testid="deals-nav-badge">{dealsPending > 9 ? "9+" : dealsPending}</span>
                    )}
                    {isActive && <span className="absolute -bottom-[21px] left-0 right-0 h-0.5 bg-ink rounded-full" />}
                  </span>
                )}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            {user && (
              <button
                onClick={() => navigate("/chat")} data-testid="header-chat-btn"
                className="relative hidden sm:grid place-items-center h-9 w-9 rounded-md text-slate-600 hover:text-ink hover:bg-slate-100 transition-colors"
              >
                <MessageCircle size={20} />
                {unread > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-crimson text-white text-[10px] font-bold grid place-items-center" data-testid="unread-badge">
                    {unread > 9 ? "9+" : unread}
                  </span>
                )}
              </button>
            )}

            {ready && !user && (
              <Button
                onClick={() => setAuthOpen(true)}
                className="bg-ink hover:bg-slate-800 text-white rounded-full px-5 h-9 text-sm"
                data-testid="header-login-btn"
              >
                Log in / Sign up
              </Button>
            )}

            {user && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="flex items-center gap-2 rounded-full pl-1 pr-2.5 py-1 hover:bg-slate-100 transition-colors" data-testid="user-menu-trigger">
                    <Avatar className="h-8 w-8 border border-slate-200">
                      <AvatarImage src={user.avatar} alt={user.name} />
                      <AvatarFallback>{user.name?.[0]}</AvatarFallback>
                    </Avatar>
                    {user.is_member && <BadgeCheck size={16} className="text-brand hidden sm:block" />}
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-60">
                  <DropdownMenuLabel className="flex flex-col gap-0.5">
                    <span className="font-semibold text-ink">{user.name}</span>
                    <span className="text-xs text-slate-500 font-normal">{user.email}</span>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <div className="px-2 py-1.5 text-xs">
                    {user.is_member ? (
                      <span className="inline-flex items-center gap-1.5 text-emerald-600 font-medium">
                        <BadgeCheck size={13} /> Campus Pass active
                      </span>
                    ) : membershipConfig?.preview_active ? (
                      <span className="inline-flex items-center gap-1.5 text-emerald-600 font-medium">
                        <BadgeCheck size={13} /> Free preview — all access
                      </span>
                    ) : (
                      <span className="text-amber-600 font-medium">No active membership</span>
                    )}
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate("/chat")} data-testid="menu-chat">
                    <MessageCircle size={15} className="mr-2" /> My Chats
                    {unread > 0 && <span className="ml-auto min-w-[18px] h-[18px] px-1 rounded-full bg-crimson text-white text-[10px] font-bold grid place-items-center">{unread > 9 ? "9+" : unread}</span>}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/deals")} data-testid="menu-deals">
                    <Package size={15} className="mr-2" /> My Deals
                    {dealsPending > 0 && <span className="ml-auto min-w-[18px] h-[18px] px-1 rounded-full bg-crimson text-white text-[10px] font-bold grid place-items-center">{dealsPending > 9 ? "9+" : dealsPending}</span>}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/marketplace")} data-testid="menu-marketplace">
                    <ShoppingBag size={15} className="mr-2" /> Marketplace
                  </DropdownMenuItem>
                  {!user.is_member && !membershipConfig?.preview_active && (
                    <DropdownMenuItem onClick={() => navigate("/membership")} data-testid="menu-membership">
                      <ArrowRight size={15} className="mr-2" /> Get Campus Pass
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={logout} className="text-crimson" data-testid="menu-logout">
                    <LogOut size={15} className="mr-2" /> Log out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}

            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="lg:hidden" data-testid="mobile-menu-btn">
                  <Menu size={22} />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-72">
                <div className="mt-6 flex flex-col gap-1">
                  {links.map((l) => (
                    <NavLink
                      key={l.path}
                      to={l.path}
                      onClick={() => setMobileOpen(false)}
                      className={({ isActive }) =>
                        `px-3 py-3 rounded-lg text-base font-medium ${isActive ? "bg-ink text-white" : "text-slate-700 hover:bg-slate-100"}`
                      }
                      data-testid={`mobile-${l.testid}`}
                      end={l.path === "/"}
                    >
                      <span className="flex items-center justify-between">
                        {l.name}
                        {l.path === "/deals" && dealsPending > 0 && <span className="min-w-[20px] h-5 px-1.5 rounded-full bg-crimson text-white text-xs font-bold grid place-items-center">{dealsPending > 9 ? "9+" : dealsPending}</span>}
                      </span>
                    </NavLink>
                  ))}
                  {user && (
                    <NavLink to="/chat" onClick={() => setMobileOpen(false)}
                      className="px-3 py-3 rounded-lg text-base font-medium text-slate-700 hover:bg-slate-100 flex items-center justify-between" data-testid="mobile-chat">
                      My Chats
                      {unread > 0 && <span className="min-w-[20px] h-5 px-1.5 rounded-full bg-crimson text-white text-xs font-bold grid place-items-center">{unread > 9 ? "9+" : unread}</span>}
                    </NavLink>
                  )}
                </div>
                {!user && (
                  <Button className="w-full mt-6 bg-ink text-white" onClick={() => { setMobileOpen(false); setAuthOpen(true); }} data-testid="mobile-login-btn">
                    Log in / Sign up
                  </Button>
                )}
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>

      {previewActive && !bannerHidden && (
        <div className="bg-ink text-white" data-testid="preview-banner">
          <div className="mx-auto max-w-7xl px-4 lg:px-8 py-2 flex items-center justify-center gap-2 text-xs sm:text-sm relative">
            <Sparkles size={14} className="text-amber-300 shrink-0" />
            <span className="text-center">
              <strong>Free preview</strong> — buying, selling &amp; chat are unlocked for everyone.
              {daysLeft > 0 ? <> Ends in <strong>{daysLeft} day{daysLeft === 1 ? "" : "s"}</strong>, then just ₹29/month.</> : <> Ending today — then just ₹29/month.</>}
            </span>
            <button onClick={dismissBanner} className="absolute right-3 text-slate-300 hover:text-white transition-colors" data-testid="preview-banner-dismiss" aria-label="Dismiss preview banner">
              <X size={15} />
            </button>
          </div>
        </div>
      )}

      <main className="flex-1">
        <Outlet context={{ openAuth: () => setAuthOpen(true) }} />
      </main>

      <footer className="border-t border-slate-200 bg-white">
        <div className="mx-auto max-w-7xl px-4 lg:px-8 py-10 grid gap-8 md:grid-cols-4">
          <div className="md:col-span-2">
            <BrandMark />
            <p className="mt-4 text-sm text-slate-500 max-w-sm leading-relaxed">
              The verified peer-to-peer marketplace built exclusively for SRM University Kattankulathur students.
              Buy and sell second-hand items safely, right on campus.
            </p>
          </div>
          <div>
            <div className="label-mono text-slate-400 mb-3">Platform</div>
            <ul className="space-y-2 text-sm text-slate-600">
              <li><Link to="/marketplace" className="hover:text-ink">Marketplace</Link></li>
              <li><Link to="/membership" className="hover:text-ink">Membership</Link></li>
              <li><Link to="/addresses" className="hover:text-ink">Pickup Points</Link></li>
            </ul>
          </div>
          <div>
            <div className="label-mono text-slate-400 mb-3">Support</div>
            <ul className="space-y-2 text-sm text-slate-600">
              <li><Link to="/help" className="hover:text-ink">Help Center</Link></li>
              <li className="text-slate-400">Campus Safety</li>
              <li className="text-slate-400">RBI-compliant payments</li>
            </ul>
          </div>
        </div>
        <div className="border-t border-slate-100 py-5 text-center text-xs text-slate-400">
          © {new Date().getFullYear()} SRM Marketplace · Made by students, for students · Not affiliated with official SRMIST administration
        </div>
      </footer>

      <AuthDialog open={authOpen} onOpenChange={setAuthOpen} />
    </div>
  );
}
